import { Box, Center, HStack, VStack, useDisclosure } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import Button from 'components/Button';
import Icon from 'components/Icon';
import Text from 'components/Text';
import { useMediaQueries } from 'layout/MediaQueriesProvider';

import CodeEditor, { formatCode } from './CodeEditor/CodeEditor';
import CodeFrame from './CodeFrame';
import { useDevSandbox } from './DevSandboxProvider';
import FloatingCodeButton from './FloatingCodeButton';
import MerlinOverlay from './Merlin/MerlinOverlay';
import MultiPaneEditor from './MultiPaneEditor';
import PaneTabs from './PaneTabs';
import TestCases from './TestCases';
import useSandboxTheme from './useSandboxTheme';

const DevSandboxContent = () => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  // For left-right/right-left layouts: editor width percentage (default 50%)
  // For top-bottom layout: editor height percentage (default 30%)
  const [editorSize, setEditorSize] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const codeMobileModal = useDisclosure();
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  // Use ref to track current size during drag to avoid stale closure issues
  const editorSizeRef = useRef(editorSize);

  const { isSmallScreen } = useMediaQueries();
  const { colors } = useSandboxTheme();

  const {
    mode,
    externalCodeUrl,
    codeTabs: { html, css, js, tabNames },
    handlers: { onEditorSettingsChange, onHtmlChange, onCssChange, onJsChange },
    iframe: { codeFrameUrl },
    flags: { isExternalCode },
    footer: { hideTools },
    editorSettings,
    merlin: { isOpen: isMerlinOpen, onToggle: merlinToggle, config: merlinConfig },
    initialLayout,
  } = useDevSandbox();

  // When initialLayout is set (e.g. preview-only for solutions/prototypes),
  // it takes precedence until dismissed via "Edit Code" button
  const [isInitialLayoutActive, setIsInitialLayoutActive] = useState(Boolean(initialLayout));
  const layout = (isInitialLayoutActive && initialLayout) ? initialLayout : editorSettings.layout;
  const isPreviewOnly = layout === 'preview-only';
  const isHorizontalSplit = layout === 'left-right' || layout === 'right-left';
  const isReversed = layout === 'right-left';

  // Reset editor size when layout changes (30% for top-bottom, 50% for horizontal)
  useEffect(() => {
    const newSize = isHorizontalSplit ? 50 : 30;
    setEditorSize(newSize);
    editorSizeRef.current = newSize;
  }, [layout, isHorizontalSplit]);

  // Desktop shortcut handlers for MultiPaneEditor mode
  // CodeEditor handles shortcuts in mobile modal, but MultiPaneEditor doesn't have its own handlers
  useEffect(() => {
    if (isSmallScreen) return;

    const handler = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod || !e.shiftKey) return;

      // Cmd/Ctrl+Shift+F: Format all visible code panes
      if (e.key.toLowerCase() === 'f') {
        e.preventDefault();
        e.stopPropagation();
        const tabSize = editorSettings.tabSize;
        if (html) onHtmlChange(formatCode(html, 0, tabSize));
        if (css) onCssChange(formatCode(css, 1, tabSize));
        if (js) onJsChange(formatCode(js, 2, tabSize));
      }

      // Cmd/Ctrl+Shift+S: Save shortcut (format first if autoFormat enabled)
      if (e.key.toLowerCase() === 's') {
        e.preventDefault();
        e.stopPropagation();
        if (editorSettings.autoFormat) {
          const tabSize = editorSettings.tabSize;
          if (html) onHtmlChange(formatCode(html, 0, tabSize));
          if (css) onCssChange(formatCode(css, 1, tabSize));
          if (js) onJsChange(formatCode(js, 2, tabSize));
        }
        window.dispatchEvent(new CustomEvent('bds:save-shortcut'));
      }

      // Cmd/Ctrl+Shift+Enter: Run tests (practice problems only)
      if (mode === 'script-practice' && e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent('bds:run-tests'));
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mode, isSmallScreen, html, css, js, tabNames, editorSettings.tabSize, editorSettings.autoFormat, onHtmlChange, onCssChange, onJsChange]);

  // Handler to switch from preview-only to editor layout
  // Dismisses initialLayout override, falls back to user's saved layout
  const handleShowEditor = () => {
    if (isInitialLayoutActive) {
      setIsInitialLayoutActive(false);
    } else {
      onEditorSettingsChange({ layout: 'left-right' });
    }
  };

  // Unified resize handler - uses absolute position calculation
  const handleDividerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!containerRef.current) return;

      setIsDragging(true);

      const isHorizontal = isHorizontalSplit;
      const reversed = isReversed;
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      let isActive = true;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isActive) return;

        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
          if (!isActive) return;

          // Calculate position as percentage directly from mouse position
          let percent: number;

          if (isHorizontal) {
            const mouseX = moveEvent.clientX - containerRect.left;
            percent = (mouseX / containerRect.width) * 100;
            // For reversed layout, preview is on left, so editor percent is inverted
            if (reversed) {
              percent = 100 - percent;
            }
          } else {
            const mouseY = moveEvent.clientY - containerRect.top;
            percent = (mouseY / containerRect.height) * 100;
          }

          // Clamp between 10% and 90%
          const clampedPercent = Math.max(10, Math.min(90, percent));
          editorSizeRef.current = clampedPercent;
          setEditorSize(clampedPercent);
        });
      };

      const handleMouseUp = () => {
        isActive = false;
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        setIsDragging(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.body.style.cursor = isHorizontal ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [isHorizontalSplit, isReversed]
  );

  // Render the multi-pane editor
  const renderEditor = () => {
    // For horizontal split (left-right, right-left): editors stacked vertically
    // For vertical split (top-bottom): editors side-by-side horizontally
    const editorOrientation = isHorizontalSplit ? 'vertical' : 'horizontal';

    return (
      <MultiPaneEditor
        html={html || ''}
        css={css || ''}
        js={js || ''}
        onHtmlChange={onHtmlChange}
        onCssChange={onCssChange}
        onJsChange={onJsChange}
        editorSettings={editorSettings}
        tabLabels={tabNames || ['HTML', 'CSS', 'JS']}
        orientation={editorOrientation}
      />
    );
  };

  // Render external code message
  const renderExternalCodeMessage = () => (
    <Center height="100%" bg={colors.editorChromeBg}>
      <VStack spacing={3} px={4}>
        <Text fontSize="large" textAlign="center">
          The external {`solution's`} code is not available
        </Text>
        <Text fontSize="large" textAlign="center">
          in our built-in Code Editor
        </Text>
        <Button
          mt={2}
          scheme="outline"
          leftIcon={<Icon type="Code" />}
          rightIcon={<Icon type="OutsideLink" />}
          onClick={() => {
            window.open(externalCodeUrl, '_blank');
          }}
        >
          View Code
        </Button>
      </VStack>
    </Center>
  );

  // Inline divider component (same style as MultiPaneEditor)
  const renderDivider = (isHorizontal: boolean) => (
    <Box
      width={isHorizontal ? '8px' : '100%'}
      height={isHorizontal ? '100%' : '8px'}
      bg={colors.editorDivider}
      cursor={isHorizontal ? 'col-resize' : 'row-resize'}
      flexShrink={0}
      onMouseDown={handleDividerMouseDown}
      _hover={{ bg: colors.hoverBg }}
      transition={isDragging ? 'none' : 'background 0.15s ease'}
    />
  );

  // Main layout renderer
  const renderMainLayout = (includeTestCases = false) => {
    if (isSmallScreen) {
      return (
        <>
          <PaneTabs />
          {/* Mobile bottom bar — centered Code button */}
          <Box
            as="button"
            onClick={codeMobileModal.onOpen}
            position="absolute"
            bottom={4}
            left="50%"
            transform="translateX(-50%)"
            zIndex={999}
            display="flex"
            alignItems="center"
            gap="6px"
            px={4}
            py={2}
            borderRadius="full"
            bg="transparent"
            color="whiteAlpha.800"
            fontSize="13px"
            fontWeight="500"
            border="1px solid"
            borderColor="whiteAlpha.200"
            transition="all 0.15s"
            _hover={{ borderColor: 'whiteAlpha.400', color: 'white' }}
            _active={{ bg: 'whiteAlpha.100' }}
            backdropFilter="blur(8px)"
          >
            <Icon type="Code" size={14} />
            Code
          </Box>
          {/* Full-screen code editor overlay (replaces cramped 80vh modal) */}
          {codeMobileModal.isOpen && (
            <Box
              position="fixed"
              top={0}
              left={0}
              right={0}
              bottom={0}
              zIndex={1400}
              bg={colors.editorChromeBg}
              display="flex"
              flexDirection="column"
            >
              <HStack
                height="44px"
                flexShrink={0}
                px={3}
                justifyContent="space-between"
                alignItems="center"
                borderBottom="1px solid"
                borderColor={colors.shellBorder}
              >
                <Text fontSize="small" opacity="secondary">Code Editor</Text>
                <Box
                  as="button"
                  onClick={codeMobileModal.onClose}
                  p={1}
                  borderRadius="md"
                  color="whiteAlpha.700"
                  _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
                  transition="all 0.15s"
                  aria-label="Close code editor"
                >
                  <Icon type="Close" size={18} />
                </Box>
              </HStack>
              <Box flex={1} overflow="hidden" position="relative">
                <Box position="absolute" top={0} left={0} right={0} bottom={0}>
                  <CodeEditor isAccordionOpen={includeTestCases && isAccordionOpen} />
                  {includeTestCases && (
                    <TestCases isAccordionOpen={isAccordionOpen} onAccordionOpen={setIsAccordionOpen} />
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </>
      );
    }

    // Preview-only mode
    if (isPreviewOnly) {
      return (
        <Box position="relative" width="100%" height="100%">
          {isMerlinOpen ? (
            <MerlinOverlay />
          ) : (
            <>
              <PaneTabs />
              {!hideTools && <FloatingCodeButton onClick={handleShowEditor} />}
            </>
          )}
          {/* Mount MerlinOverlay hidden when merlinConfig has autoTrigger but panel is closed,
              so streaming can work in preview-only mode */}
          {!isMerlinOpen && merlinConfig?.autoTrigger && (
            <MerlinOverlay hidden />
          )}
        </Box>
      );
    }

    // Horizontal split: Editor (left/right) | Preview (right/left)
    if (isHorizontalSplit) {
      const editorPane = (
        <Box
          data-tour="code-editor"
          width={`${editorSize}%`}
          height="100%"
          overflow="hidden"
          position="relative"
          display="flex"
          flexDirection="column"
          transition={isDragging ? 'none' : 'width 0.1s ease-out'}
        >
          {isMerlinOpen ? (
            <MerlinOverlay />
          ) : (
            <>
              {isExternalCode ? renderExternalCodeMessage() : renderEditor()}
              {includeTestCases && (
                  <TestCases isAccordionOpen={isAccordionOpen} onAccordionOpen={setIsAccordionOpen} />
              )}
            </>
          )}
          {!isMerlinOpen && merlinConfig?.autoTrigger && (
            <MerlinOverlay hidden />
          )}
        </Box>
      );

      const previewPane = (
        <Box
          data-tour="preview-panel"
          width={`${100 - editorSize}%`}
          height="100%"
          overflow="hidden"
          position="relative"
          transition={isDragging ? 'none' : 'width 0.1s ease-out'}
        >
          <PaneTabs />
        </Box>
      );

      return (
        <HStack
          ref={containerRef}
          spacing={0}
          height="100%"
          width="100%"
          overflow="hidden"
          position="relative"
        >
          {isReversed ? (
            <>
              {previewPane}
              {renderDivider(true)}
              {editorPane}
            </>
          ) : (
            <>
              {editorPane}
              {renderDivider(true)}
              {previewPane}
            </>
          )}
          {/* Full-viewport overlay to prevent iframe/editor capturing mouse during resize */}
          {isDragging && <Box position="fixed" inset={0} zIndex={9999} />}
        </HStack>
      );
    }

    // Vertical split (top-bottom): Editor on top | Preview on bottom
    const previewSize = 100 - editorSize;
    return (
      <Box
        ref={containerRef}
        display="flex"
        flexDirection="column"
        height="100%"
        width="100%"
        overflow="hidden"
        position="relative"
      >
        <Box
          data-tour="code-editor"
          height={`${editorSize}%`}
          overflow="hidden"
          position="relative"
          flexShrink={0}
          display="flex"
          flexDirection="row"
          transition={isDragging ? 'none' : 'height 0.1s ease-out'}
        >
          {isMerlinOpen ? (
            <MerlinOverlay />
          ) : (
            <>
              {isExternalCode ? renderExternalCodeMessage() : renderEditor()}
              {includeTestCases && (
                  <TestCases isAccordionOpen={isAccordionOpen} onAccordionOpen={setIsAccordionOpen} />
              )}
            </>
          )}
          {!isMerlinOpen && merlinConfig?.autoTrigger && (
            <MerlinOverlay hidden />
          )}
        </Box>
        {renderDivider(false)}
        <Box
          data-tour="preview-panel"
          height={`${previewSize}%`}
          overflow="hidden"
          position="relative"
          flexShrink={0}
          transition={isDragging ? 'none' : 'height 0.1s ease-out'}
        >
          <PaneTabs />
        </Box>
        {/* Full-viewport overlay to prevent iframe/editor capturing mouse during resize */}
        {isDragging && <Box position="fixed" inset={0} zIndex={9999} />}
      </Box>
    );
  };

  switch (mode) {
    case 'script-practice':
      return renderMainLayout(true);
    case 'interactive':
      return renderMainLayout(false);
    case 'full-page':
    case 'embed':
      return <CodeFrame url={codeFrameUrl || ''} />;
    default:
      return null;
  }
};

export default DevSandboxContent;
